import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash } from "@phosphor-icons/react";
import { useQuote } from "@/lib/quote";

export default function QuoteDrawer() {
  const { drawerOpen, setDrawerOpen, items, setQuantity, removeItem, palette, setAtelierOpen } = useQuote();

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[70] bg-ink/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            data-testid="quote-drawer-backdrop"
          />
          <motion.aside
            data-testid="quote-drawer"
            className="fixed right-0 top-0 z-[80] flex h-full w-full max-w-md flex-col bg-ivory shadow-2xl"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            aria-label="My quote list"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-5">
              <h2 className="font-display text-3xl text-ink">My Quote</h2>
              <button data-testid="quote-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close quote list" className="text-ink/60 hover:text-ink">
                <X size={24} weight="light" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {items.length === 0 ? (
                <div className="pt-10 text-center" data-testid="quote-drawer-empty">
                  <p className="font-display text-2xl text-ink">Nothing gathered yet</p>
                  <p className="mx-auto mt-2 max-w-xs text-sm text-smoke">
                    Browse the collection and add the details you love. Your list keeps itself here, and nothing is final until you say so.
                  </p>
                  <Link
                    to="/shop"
                    onClick={() => setDrawerOpen(false)}
                    className="mt-6 inline-block rounded-none bg-olive px-7 py-3 text-[12px] uppercase tracking-[0.18em] text-ivory"
                    data-testid="quote-drawer-empty-shop-link"
                  >
                    Explore the Collection
                  </Link>
                </div>
              ) : (
                <ul className="space-y-5" data-testid="quote-drawer-items">
                  {items.map((i) => (
                    <li key={i.slug} className="flex gap-4 border-b border-ink/10 pb-5">
                      <img src={i.image} alt="" className="h-20 w-16 flex-none object-cover" width="64" height="80" loading="lazy" />
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-lg leading-tight text-ink">{i.title}</p>
                        {i.options && <p className="mt-0.5 truncate text-[12px] text-smoke">{i.options}</p>}
                        <div className="mt-2 flex items-center gap-3">
                          <div className="flex items-center border border-ink/20">
                            <button data-testid={`quote-qty-minus-${i.slug}`} onClick={() => setQuantity(i.slug, (i.quantity || 1) - 1)} aria-label={`Decrease quantity of ${i.title}`} className="px-2 py-1 text-ink/60 hover:text-olive">
                              <Minus size={12} />
                            </button>
                            <span className="w-8 text-center text-sm" data-testid={`quote-qty-${i.slug}`}>{i.quantity}</span>
                            <button data-testid={`quote-qty-plus-${i.slug}`} onClick={() => setQuantity(i.slug, (i.quantity || 1) + 1)} aria-label={`Increase quantity of ${i.title}`} className="px-2 py-1 text-ink/60 hover:text-olive">
                              <Plus size={12} />
                            </button>
                          </div>
                          <button
                            data-testid={`quote-remove-${i.slug}`}
                            onClick={() => removeItem(i.slug)}
                            aria-label={`Remove ${i.title}`}
                            className="text-ink/40 transition-colors hover:text-red-700"
                          >
                            <Trash size={15} weight="light" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {palette && (
                <div className="mt-6 border border-ink/10 bg-parch p-4" data-testid="quote-drawer-palette">
                  <p className="mv-label mb-2">From the Palette Atelier</p>
                  <div className="flex items-center gap-3">
                    <div className="flex">
                      {palette.colors.map((c, i) => (
                        <span key={i} className="h-7 w-7" style={{ background: c }} aria-hidden="true" />
                      ))}
                    </div>
                    <div className="text-sm">
                      <p className="text-ink">{palette.name}</p>
                      {palette.monogram && <p className="text-smoke">Monogram: {palette.monogram}</p>}
                    </div>
                    <button
                      data-testid="quote-drawer-edit-palette"
                      onClick={() => { setDrawerOpen(false); setAtelierOpen(true); }}
                      className="ml-auto border-b border-olive pb-0.5 text-[12px] uppercase tracking-[0.14em] text-olive"
                    >
                      Edit
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-ink/10 px-6 py-5">
              <p className="text-[12px] leading-relaxed text-smoke" data-testid="quote-drawer-pricing-note">
                Pricing confirmed in your proposal. Submitting an inquiry does not reserve a date or commit you to a purchase.
              </p>
              <Link
                to="/quote"
                data-testid="quote-drawer-continue"
                onClick={() => setDrawerOpen(false)}
                className={`mt-4 block rounded-none py-3.5 text-center text-[12px] uppercase tracking-[0.18em] transition-opacity ${
                  items.length ? "bg-olive text-ivory hover:opacity-90" : "pointer-events-none bg-ink/10 text-ink/40"
                }`}
              >
                Continue to My Quote
              </Link>
              <button
                data-testid="quote-drawer-keep-shopping"
                onClick={() => setDrawerOpen(false)}
                className="mt-3 w-full border border-ink/15 py-3 text-[12px] uppercase tracking-[0.18em] text-ink transition-colors hover:border-olive hover:text-olive"
              >
                Keep Browsing
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
